import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from "firebase/auth";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import ClientPortal from "./components/ClientPortal";

const ADMIN_EMAIL = "frank@ficsoriaccounting.com";

class ErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state={hasError:false,error:null}; }
  static getDerivedStateFromError(error){ return {hasError:true,error}; }
  componentDidCatch(error,info){ console.error("Portal crash:", error, info); }
  render(){
    if(this.state.hasError){
      return (
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"sans-serif",background:"#f5f5f0",flexDirection:"column",gap:12,padding:24}}>
          <div style={{fontSize:32}}>⚠️</div>
          <div style={{fontSize:15,fontWeight:500,color:"#C0111F"}}>Something went wrong</div>
          <div style={{fontSize:12,color:"#888",textAlign:"center",maxWidth:400}}>{this.state.error?.message}</div>
          <button onClick={()=>window.location.reload()} style={{marginTop:8,padding:"8px 16px",background:"#C0111F",color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontSize:13}}>Reload Portal</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [clientName, setClientName] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    const unsub = onAuthStateChanged(auth, async (u)=>{
      if(u){
        setUser(u);
        // Admin check — instant, no Firestore needed
        if(u.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()){
          setUserRole("admin");
          setClientName(null);
          setLoading(false);
          return;
        }
        // For everyone else — check if client, otherwise treat as team
        try {
          const snap = await getDocs(
            query(collection(db,"clients"), where("email","==",u.email.toLowerCase()))
          );
          if(!snap.empty){
            const clientData = snap.docs[0].data();
            if(!clientData.portalAccepted){
              try{
                await updateDoc(doc(db,"clients",snap.docs[0].id),{
                  portalAccepted:true, portalAcceptedAt:new Date().toISOString()
                });
                console.log("Portal accepted updated for:", clientData.name);
              }catch(e){ console.error("Failed to update portalAccepted:", e.code, e.message); }
            }
            setUserRole("client");
            setClientName(clientData.name);
          } else {
            // Not a client — must be team member
            setUserRole("team");
            setClientName(null);
          }
        } catch(e){
          console.error("Role check failed:", e);
          // On any error — default to team view, never white screen
          setUserRole("team");
          setClientName(null);
        }
      } else {
        setUser(null);
        setUserRole(null);
        setClientName(null);
      }
      setLoading(false);
    });
    return ()=>unsub();
  },[]);

  const handleLogin = async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  if(loading){
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"sans-serif",background:"#f5f5f0",flexDirection:"column",gap:12}}>
        <div style={{width:32,height:32,border:"3px solid #e0dfd8",borderTop:"3px solid #C0111F",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
        <div style={{fontSize:13,color:"#888"}}>Loading your portal...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if(!user) return <Login onLogin={handleLogin}/>;
  if(userRole === "client") return <ErrorBoundary><ClientPortal user={user} onLogout={handleLogout} clientName={clientName}/></ErrorBoundary>;
  return <ErrorBoundary><Dashboard user={user} onLogout={handleLogout}/></ErrorBoundary>;
}
